import {
  CButton,
  CCol,
  CForm,
  CFormFeedback,
  CLoadingButton,
  CMultiSelect,
  CRow,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilPeople } from '@coreui/icons'
import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import PlayerService from 'src/service/PlayerService'
import ToastComponent from 'src/components/common/TaostComponent'
import CommonService from 'src/service/CommonService'
import ImageDropzone, {
  SUPPORTED_IMAGE_FORMATS,
  MAX_IMAGE_SIZE,
} from 'src/components/common/ImageDropzone'
import PlayerReviewTable from './BulkImage/PlayerReviewTable'
import SectionBox from './BulkImage/SectionBox'

const PlayerBulkImageUpload = (props) => {
  const [multiOption, setMultiOption] = useState([])
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null)
  const [loader, setLoader] = useState(false)
  const [selectKey, setSelectKey] = useState(0)

  useEffect(() => {
    CommonService.clubPlayers().then((result) => {
      setMultiOption(result.data)
    })
  }, [])

  const validationSchema = Yup.object().shape({
    player: Yup.array()
      .of(Yup.string())
      .min(1, 'Please select at least one Player')
      .required('Please select Player'),
    image: Yup.mixed()
      .nullable(true)
      .required('Image is required')
      .test('fileSize', 'File size too large, max file size is 2 MB', (file) => {
        if (file) {
          return file.size <= MAX_IMAGE_SIZE
        }
        return true
      })
      .test(
        'type',
        'Invalid file format, only JPG, PNG or WEBP is allowed',
        (value) => !value || (value && SUPPORTED_IMAGE_FORMATS.includes(value?.type)),
      ),
  })

  const formik = useFormik({
    initialValues: {
      player: [],
      image: null,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (data) => {
      var formData = new FormData()
      data.player.forEach((playerValue) => {
        formData.append('player[]', playerValue)
      })
      formData.append('image', data.image)
      setLoader(true)
      PlayerService.saveBulkPlayerImage(formData).then((res) => {
        if (res.status === 200) {
          resetAll()
          props.setRefreshKey((prev) => prev + 1)
          ToastComponent(res.message, 'success')
        } else {
          ToastComponent(res.message, 'error')
        }
        setLoader(false)
      })
    },
  })

  const resetAll = () => {
    formik.resetForm({
      values: {
        player: [],
        image: null,
      },
    })
    setImagePreviewUrl(null)
    // CMultiSelect keeps its own internal tag state, so a value-prop change
    // alone won't clear the visible chips - force a remount instead.
    setSelectKey((prev) => prev + 1)
  }

  const setImageFile = (file) => {
    formik.setTouched({ ...formik.touched, image: true })
    formik.setFieldValue('image', file)
    const reader = new FileReader()
    reader.onload = () => setImagePreviewUrl(reader.result)
    reader.readAsDataURL(file)
  }

  const selectedPlayers = multiOption.filter((obj) => formik.values.player.includes(obj.value))
  const reviewPlayers = selectedPlayers.map((item) => ({
    id: item.value,
    name: item.text,
    team_name: item.team_name,
    image: item.image,
    is_avatar: item.is_avatar,
  }))

  return (
    <CForm onSubmit={formik.handleSubmit}>
      <CRow className="g-4">
        <CCol md={6}>
          <SectionBox title="1. Select Players">
            <CMultiSelect
              key={selectKey}
              options={multiOption}
              selectionType="tags"
              name="player"
              placeholder="Search and select players"
              className={formik.errors.player && formik.touched.player ? 'is-invalid' : ''}
              onChange={(selectedOptions) => {
                formik.setFieldValue(
                  'player',
                  selectedOptions.map((option) => option.value),
                )
              }}
              onBlur={() => {
                formik.setFieldTouched('player', true)
              }}
              value={selectedPlayers}
            />
            {formik.errors.player && formik.touched.player && (
              <CFormFeedback invalid>{formik.errors.player}</CFormFeedback>
            )}
            <div className="text-medium-emphasis small d-flex align-items-center mt-2">
              <CIcon icon={cilPeople} className="me-1" />
              {selectedPlayers.length} players selected
            </div>
          </SectionBox>
        </CCol>

        <CCol md={6}>
          <SectionBox title="2. Upload Image">
            <ImageDropzone
              onChange={setImageFile}
              error={formik.touched.image ? formik.errors.image : null}
              previewUrl={imagePreviewUrl}
            />
          </SectionBox>
        </CCol>
      </CRow>

      <PlayerReviewTable players={reviewPlayers} newImagePreviewUrl={imagePreviewUrl} />

      <div className="d-flex justify-content-end gap-2 mt-4">
        <CButton color="secondary" variant="outline" type="button" onClick={resetAll}>
          Cancel
        </CButton>
        <CLoadingButton type="submit" color="primary" loading={loader}>
          Apply Image to {selectedPlayers.length} Players
        </CLoadingButton>
      </div>
    </CForm>
  )
}

export default PlayerBulkImageUpload
